import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate or retrieve visitor ID from localStorage
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID (persists for the browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

// Check if this is a new visitor
const isNewVisitor = () => {
  return !localStorage.getItem('returning_visitor');
};

// Mark as returning visitor
const markReturningVisitor = () => {
  localStorage.setItem('returning_visitor', 'true');
};

// Get UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content')
  };
};

// Check if cookie consent was given
const hasConsent = () => {
  return localStorage.getItem('cookie_consent') === 'accepted';
};

const VisitorTracker = () => {
  const location = useLocation();
  const pageLoadTime = useRef(Date.now());
  const lastPath = useRef(null);

  useEffect(() => {
    // Only track if consent was given
    if (!hasConsent()) {
      return;
    }

    const trackVisit = async () => {
      // Don't track admin pages
      if (location.pathname.startsWith('/admin')) {
        return;
      }

      const visitorId = getVisitorId();
      const sessionId = getSessionId();
      const newVisitor = isNewVisitor();
      const utmParams = getUtmParams();

      // Track time on previous page
      if (lastPath.current && lastPath.current !== location.pathname) {
        const timeSpent = Math.floor((Date.now() - pageLoadTime.current) / 1000);
        try {
          await fetch(`${API}/track/time?session_id=${sessionId}&page_path=${encodeURIComponent(lastPath.current)}&time_spent=${timeSpent}`, {
            method: 'POST'
          });
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.debug('Time tracking failed:', e);
        }
      }

      // Reset page load time
      pageLoadTime.current = Date.now();
      lastPath.current = location.pathname;

      const trackingData = {
        session_id: sessionId,
        visitor_id: visitorId,
        is_new_visitor: newVisitor,
        page_url: window.location.href,
        page_path: location.pathname,
        page_title: document.title,
        referrer: document.referrer || null,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        language: navigator.language || navigator.userLanguage,
        ...utmParams
      };

      try {
        await fetch(`${API}/track/visit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(trackingData)
        });

        // Mark as returning visitor after first track
        if (newVisitor) {
          markReturningVisitor();
        }
      } catch (e) {
        // Silently fail - don't disrupt user experience
        if (process.env.NODE_ENV === 'development') console.debug('Tracking failed:', e);
      }
    };

    trackVisit();

    // Track time spent when leaving page
    const handleBeforeUnload = () => {
      if (hasConsent() && lastPath.current) {
        const timeSpent = Math.floor((Date.now() - pageLoadTime.current) / 1000);
        navigator.sendBeacon(
          `${API}/track/time?session_id=${getSessionId()}&page_path=${encodeURIComponent(lastPath.current)}&time_spent=${timeSpent}`
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [location.pathname]); // All other refs (hasConsent, getVisitorId, etc.) are module-level stable constants

  return null; // This component doesn't render anything
};

export default VisitorTracker;
